import { useMutation } from '@apollo/client/react';
import { type ApiUser, VerifyStylistCredentialsDocument } from '@drycut/graphql';
import { ActionButtons, Button, Para, Select, Stack, TextField } from '@drycut/ui-components';
import { useState } from 'react';

import type { SelectOption } from './types';

import { CouponSummary } from './CouponSummary';

type CouponSummaryData = {
  couponType: string;
  customer: { name: string; phoneNumber: string };
  reason: string;
};

type Props = {
  apiUsers: ApiUser[];
  couponSummary: CouponSummaryData;
  creatingCoupon: boolean;
  onAuthenticated: (stylistId: string) => void;
  onBack: () => void;
  onCancel: () => void;
};

export function StylistAuthStep({
  onAuthenticated,
  onBack,
  onCancel,
  couponSummary,
  apiUsers,
  creatingCoupon,
}: Props): React.ReactElement {
  const [selectedStylist, setSelectedStylist] = useState<SelectOption | undefined>();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<null | string>(null);

  const [verifyCredentials, { loading }] = useMutation(VerifyStylistCredentialsDocument);

  const stylistOptions: SelectOption[] = apiUsers.map((user: { id: string; name: string }) => ({
    id: user.id,
    value: user.id,
    label: user.name,
  }));

  const handleStylistChange = (option: SelectOption) => {
    setSelectedStylist(option);
    setError(null);
  };

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!selectedStylist) {
      setError('Please select a stylist');
      return;
    }

    if (!password) {
      setError('Please enter password');
      return;
    }

    // Tries to verify the credentials with the backend and calls onAuthenticated if successful
    // If there are errors, it sets the error message to be displayed
    try {
      const result = await verifyCredentials({
        variables: {
          input: {
            apiUserId: selectedStylist.value,
            password,
          },
        },
      });

      if (result.data?.verifyStylistCredentials?.success) {
        onAuthenticated(selectedStylist.value);
      } else if (result.data?.verifyStylistCredentials?.errors?.length) {
        const errorMsg = result.data.verifyStylistCredentials.errors
          .map((e: { message: string }) => e.message)
          .join(', ');
        setError(errorMsg);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler bei der Authentifizierung';
      setError(errorMessage);
    }
  };

  return (
    <Stack as="form" gap={4} className="px-10" noValidate onSubmit={handleSubmit}>
      {/* Coupon summary */}
      <Stack gap={2}>
        <CouponSummary
          customer={
            couponSummary.customer.name
              ? `${couponSummary.customer.name} (${couponSummary.customer.phoneNumber})`
              : undefined
          }
          couponType={couponSummary.couponType}
        />

        {couponSummary.reason && (
          <Para>
            <span className="font-aribaubold">Notitz:</span> {couponSummary.reason}
          </Para>
        )}

        <Button
          type="button"
          fill="ghost"
          size="small"
          onClick={onBack}
          disabled={loading}
          className="w-fit mx-auto"
        >
          Gutschein Details anpassen
        </Button>
      </Stack>

      <Para className="text-left">Bitte mit DRYCUT login autentifizieren</Para>

      <Stack gap={4}>
        {/* Stylist Selection */}
        <Stack gap={2}>
          <Select
            label="Stylist"
            name="stylist"
            options={stylistOptions}
            selected={selectedStylist}
            onChange={handleStylistChange}
            prompt="Auswählen"
            disabled={loading || creatingCoupon}
          />
        </Stack>

        {/* Password Field */}
        <Stack gap={2}>
          <TextField
            invalid={Boolean(error)}
            name="password"
            label="Password"
            type="password"
            placeholder="Passwort eingeben"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            disabled={loading || creatingCoupon}
          />

          {/* Error Message */}
          {error && (
            <Para className="text-red-600 font-bold" size="sm">
              {error}
            </Para>
          )}
        </Stack>
      </Stack>

      <ActionButtons>
        <Button
          type="submit"
          loading={loading || creatingCoupon}
          disabled={!selectedStylist || !password}
        >
          Gutschein erstellen
        </Button>

        <Button
          type="button"
          fill="outlined"
          onClick={onCancel}
          disabled={loading || creatingCoupon}
        >
          Cancel
        </Button>
      </ActionButtons>
    </Stack>
  );
}
